import React from 'react';
import { Link } from 'react-router-dom';
import { Clock, Calendar, Tag, ArrowRight, BookOpen, FileText, Settings, Briefcase, Scale, Code2, Shield, Eye, Monitor, Smartphone, CheckSquare, Zap } from 'lucide-react';
import { BlogPost, BlogCategory } from '../../types/blog';
import { formatDate, getCategoryColor } from '../../utils/blog';

interface BlogCardProps {
  post: BlogPost;
}

// Fixed mapping: each slug gets a guaranteed unique gradient + icon combo
const postStyles: Record<string, { gradient: string; icon: string }> = {
  'web-accessibility-2025':              { gradient: 'from-blue-500 to-indigo-700',    icon: 'BookOpen' },
  '10-haeufigste-accessibility-fehler':  { gradient: 'from-rose-500 to-pink-700',     icon: 'CheckSquare' },
  '10-most-common-accessibility-errors': { gradient: 'from-amber-500 to-orange-700',  icon: 'FileText' },
  'ai-and-accessibility-2025':           { gradient: 'from-violet-500 to-purple-700',  icon: 'Zap' },
  'compliance-fatigue':                  { gradient: 'from-teal-500 to-cyan-700',      icon: 'Settings' },
  '508-compliance-checklist':            { gradient: 'from-emerald-500 to-green-700',  icon: 'Shield' },
  'ada-website-compliance':              { gradient: 'from-red-500 to-rose-700',       icon: 'Scale' },
  'screen-reader-testing':              { gradient: 'from-sky-500 to-blue-700',       icon: 'Eye' },
};

const iconComponents: Record<string, React.ReactNode> = {
  BookOpen:    <BookOpen className="w-16 h-16" />,
  FileText:    <FileText className="w-16 h-16" />,
  Settings:    <Settings className="w-16 h-16" />,
  Briefcase:   <Briefcase className="w-16 h-16" />,
  Scale:       <Scale className="w-16 h-16" />,
  Code2:       <Code2 className="w-16 h-16" />,
  Shield:      <Shield className="w-16 h-16" />,
  Eye:         <Eye className="w-16 h-16" />,
  Monitor:     <Monitor className="w-16 h-16" />,
  Smartphone:  <Smartphone className="w-16 h-16" />,
  CheckSquare: <CheckSquare className="w-16 h-16" />,
  Zap:         <Zap className="w-16 h-16" />,
};

// Fallback gradients for future posts not yet in the map
const fallbackGradients = [
  'from-fuchsia-500 to-violet-700',
  'from-lime-500 to-green-700',
  'from-orange-500 to-red-700',
  'from-cyan-500 to-teal-700',
  'from-pink-500 to-rose-700',
  'from-indigo-500 to-blue-700',
];

const categoryIconMap: Record<BlogCategory, string> = {
  [BlogCategory.FUNDAMENTALS]: 'BookOpen',
  [BlogCategory.PRACTICAL_GUIDES]: 'FileText',
  [BlogCategory.TOOLS]: 'Settings',
  [BlogCategory.BUSINESS]: 'Briefcase',
  [BlogCategory.LEGAL]: 'Scale',
  [BlogCategory.FRAMEWORKS]: 'Code2',
};

export const BlogCard: React.FC<BlogCardProps> = ({ post }) => {
  const getPostStyle = (slug: string, category: BlogCategory) => {
    const style = postStyles[slug];
    if (style) {
      return {
        gradient: style.gradient,
        icon: iconComponents[style.icon] || <BookOpen className="w-16 h-16" />,
      };
    }
    // Fallback for unknown slugs
    let hash = 0;
    for (let i = 0; i < slug.length; i++) {
      hash = ((hash << 5) - hash) + slug.charCodeAt(i);
      hash |= 0;
    }
    return {
      gradient: fallbackGradients[Math.abs(hash) % fallbackGradients.length],
      icon: iconComponents[categoryIconMap[category]] || <BookOpen className="w-16 h-16" />,
    };
  };

  const categoryStyle = getPostStyle(post.slug, post.category);

  return (
    <article className="bg-white rounded-lg shadow-md hover:shadow-xl transition-shadow duration-300 overflow-hidden group">
      {/* Featured Image or Placeholder */}
      <div className="relative h-48 overflow-hidden">
        {post.image ? (
          <>
            <img
              src={post.image}
              alt={`Cover image for ${post.title}`}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
              loading="lazy"
            />
            {post.featured && (
              <span className="absolute top-4 left-4 bg-yellow-500 text-white px-3 py-1 rounded-full text-sm font-semibold shadow-lg">
                Featured
              </span>
            )}
          </>
        ) : (
          <div className={`w-full h-full bg-gradient-to-br ${categoryStyle.gradient} flex items-center justify-center group-hover:scale-105 transition-transform duration-300`}>
            <div className="text-white opacity-50 group-hover:opacity-70 transition-opacity">
              {categoryStyle.icon}
            </div>
            {post.featured && (
              <span className="absolute top-4 left-4 bg-yellow-500 text-white px-3 py-1 rounded-full text-sm font-semibold shadow-lg">
                Featured
              </span>
            )}
          </div>
        )}
      </div>
      
      <div className="p-6">
        {/* Category Badge */}
        <div className="flex items-center gap-2 mb-3">
          <span className={`px-3 py-1 rounded-full text-xs font-medium ${getCategoryColor(post.category)}`}>
            {post.category}
          </span>
        </div>
        
        {/* Title */}
        <h2 className="text-xl font-bold mb-3 line-clamp-2 group-hover:text-blue-600 transition-colors">
          <Link to={`/blog/${post.slug}`} className="focus:outline-none focus:ring-2 focus:ring-blue-500 rounded">
            {post.title}
          </Link>
        </h2>
        
        {/* Description */}
        <p className="text-gray-600 mb-4 line-clamp-3">
          {post.description}
        </p>
        
        {/* Meta Information */}
        <div className="flex items-center gap-4 text-sm text-gray-500 mb-4">
          <div className="flex items-center gap-1">
            <Calendar className="w-4 h-4" aria-hidden="true" />
            <time dateTime={post.date}>{formatDate(post.date)}</time>
          </div>
          <div className="flex items-center gap-1">
            <Clock className="w-4 h-4" aria-hidden="true" />
            <span>{post.readingTime.text}</span>
          </div>
        </div>
        
        {/* Tags */}
        {post.tags.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-4">
            {post.tags.slice(0, 3).map((tag) => (
              <span
                key={tag}
                className="inline-flex items-center gap-1 text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded"
              >
                <Tag className="w-3 h-3" aria-hidden="true" />
                {tag}
              </span>
            ))}
            {post.tags.length > 3 && (
              <span className="text-xs text-gray-500">+{post.tags.length - 3} more</span>
            )}
          </div>
        )}
        
        {/* Read More Link */}
        <Link
          to={`/blog/${post.slug}`}
          className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-800 font-medium group/link focus:outline-none focus:ring-2 focus:ring-blue-500 rounded px-2 py-1 -ml-2"
          aria-label={`Read article: ${post.title}`}
        >
          Read More
          <ArrowRight className="w-4 h-4 group-hover/link:translate-x-1 transition-transform" aria-hidden="true" />
        </Link>
      </div>
    </article>
  );
};
